import React from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import SEO from '../components/SEO';
import PublicProductSearch from '../components/PublicProductSearch';
import logo from '../assets/images/logo.png';
import { trackGoal } from '../utils/metrika';
import {
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  CheckCircleIcon,
  CpuChipIcon,
  CloudArrowUpIcon,
  DocumentMagnifyingGlassIcon,
  CogIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ArrowLongRightIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon, ClockIcon as ClockSolidIcon } from '@heroicons/react/24/solid';

export const HomePageHeader: React.FC = () => {
  const { isAuthenticated, logout } = useAppContext();

  return (
    <header className="bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center gap-3">
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 text-xl sm:text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors duration-300 whitespace-nowrap">
            <img
              src={logo}
              alt="Get-Goods.ai Logo"
              className="h-9 sm:h-10 w-auto"
            />
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-extrabold">
              Get-Goods.ai
            </span>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/podbor-elektrotehniki-po-specifikacii"
              className="hidden md:inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors duration-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-50"
            >
              Подбор по спецификации
            </Link>
            <Link
              to="/raspoznavanie-odnolineynyh-shem"
              className="hidden md:inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors duration-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-50"
            >
              Однолинейные схемы
            </Link>
            {isAuthenticated ? (
              <>
                <Link
                  to="/my-applications"
                  className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors duration-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-50"
                >
                  <UserCircleIcon className="h-5 w-5 mr-1.5" />
                  Личный кабинет
                </Link>
                <button
                  onClick={logout}
                  className="inline-flex items-center bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 mr-1.5 transform rotate-180" />
                  Выйти
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => trackGoal('login_click', { source: 'home_header_login' })}
                  className="hidden sm:inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors duration-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-50"
                >
                  Войти
                </Link>
                <Link
                  to="/login"
                  onClick={() => trackGoal('login_click', { source: 'home_header_start' })}
                  className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4 sm:px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <SparklesIcon className="h-5 w-5 mr-1.5" />
                  <span className="hidden sm:inline">Начать работу</span>
                  <span className="sm:hidden">Начать</span>
                </Link>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

const HeroSection: React.FC = () => {
  const { isAuthenticated } = useAppContext();

  return (
    <section className="relative min-h-screen flex items-start justify-center pt-24 md:pt-28 pb-16 bg-gradient-to-br from-blue-50 via-white to-purple-50 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234F46E5' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-5 leading-tight">
            ИИ-подбор{' '}
            <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              электротехники
            </span>
            {' '}по спецификациям и схемам
          </h1>
          
          <p className="text-lg md:text-xl text-gray-600 mb-6 leading-relaxed max-w-3xl mx-auto">
            Загрузите спецификацию, перечень номенклатуры, однолинейную схему или PDF-раздел проектной документации.
            Get-Goods.ai распознаёт позиции и подбирает реальные электротехнические изделия:
            артикулы производителей, бренды, аналоги и цены.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            {isAuthenticated ? (
              <Link
                to="/my-applications"
                onClick={() => trackGoal('upload_click', { source: 'home_hero_cta' })}
                className="inline-flex items-center bg-gray-900 hover:bg-gray-800 text-white px-8 py-3.5 rounded-lg text-base font-semibold transition-colors duration-300 shadow-lg"
              >
                <CloudArrowUpIcon className="h-6 w-6 mr-2" />
                Загрузить документ
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => trackGoal('login_click', { source: 'home_hero_upload_cta' })}
                  className="inline-flex items-center bg-gray-900 hover:bg-gray-800 text-white px-8 py-3.5 rounded-lg text-base font-semibold transition-colors duration-300 shadow-lg"
                >
                  <CloudArrowUpIcon className="h-6 w-6 mr-2" />
                  Загрузить документ
                </Link>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto mb-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1">Excel</div>
              <div className="text-sm text-gray-600">Спецификации и перечни</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-1">PDF</div>
              <div className="text-sm text-gray-600">Проектная документация</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">SKU</div>
              <div className="text-sm text-gray-600">Артикулы и бренды</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 mb-1">₽</div>
              <div className="text-sm text-gray-600">Цены из доступных источников</div>
            </div>
          </div>

          <div className="mb-12">
            <PublicProductSearch />
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturesSection: React.FC = () => {
  const features = [
    {
      icon: DocumentMagnifyingGlassIcon,
      title: "Разбор спецификаций",
      description: "ИИ извлекает позиции, количество, единицы измерения и технические требования из спецификаций и перечней оборудования",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: CpuChipIcon,
      title: "Подбор артикулов",
      description: "Автоматический подбор реальных электротехнических изделий, артикулов производителей, брендов и аналогов",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: ChartBarIcon,
      title: "Цены и предложения",
      description: "Сопоставление позиций с доступными ценами, поставщиками и источниками данных для закупки",
      color: "from-green-500 to-green-600"
    },
    {
      icon: ClockSolidIcon,
      title: "Быстрая обработка", 
      description: "Результаты готовы за несколько минут вместо часов ручной работы",
      color: "from-yellow-500 to-yellow-600"
    },
    {
      icon: ShieldCheckIcon,
      title: "Проверенные данные",
      description: "Актуальная база данных с проверенными техническими характеристиками",
      color: "from-red-500 to-red-600"
    },
    {
      icon: CogIcon,
      title: "Гибкие настройки",
      description: "Возможность корректировки результатов и настройки параметров поиска",
      color: "from-indigo-500 to-indigo-600"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Возможности платформы
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Современные технологии искусственного интеллекта для эффективного подбора электротехники
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group p-8 bg-gray-50 hover:bg-white rounded-2xl transition-all duration-300 hover:shadow-xl hover:-translate-y-2 border border-gray-100"
            >
              <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${feature.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SpecificationMatchingSection: React.FC = () => {
  const capabilities = [
    "подбор артикулов производителей по строкам спецификации",
    "сравнение брендов, аналогов и технических характеристик",
    "поиск цен и источников по доступным данным",
    "повторный подбор при изменении бренда или требований"
  ];

  return (
    <section id="specification-matching" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-3">
              Основной сценарий платформы
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Подбор по спецификации: артикулы, бренды и цены
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Get-Goods.ai помогает перейти от перечня оборудования к реальным товарам:
              подобрать артикулы производителей, увидеть бренды, аналоги, характеристики
              и цены электротехнической продукции.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {capabilities.map((capability) => (
                <div key={capability} className="flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{capability}</span>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to="/podbor-elektrotehniki-po-specifikacii"
                className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300 shadow-lg"
              >
                Подробнее о подборе по спецификации
                <ChartBarIcon className="h-5 w-5 ml-2" />
              </Link>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg bg-gray-50 shadow-xl overflow-hidden">
            <div className="bg-gray-900 text-white px-5 py-4 font-semibold">
              Пример результата
            </div>
            <div className="divide-y divide-gray-200">
              {[
                ["Автомат 3P C25", "бренд + артикул", "цена"],
                ["Кабель ВВГнг(A)-LS", "варианты производителей", "поставщик"],
                ["Щит IP54", "аналоги и характеристики", "сравнение"]
              ].map(([source, product, price]) => (
                <div key={source} className="grid grid-cols-3 gap-3 p-5 bg-white text-sm">
                  <div>
                    <div className="text-gray-500">Спецификация</div>
                    <div className="font-semibold text-gray-900">{source}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Подбор</div>
                    <div className="font-semibold text-gray-900">{product}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Данные</div>
                    <div className="font-semibold text-gray-900">{price}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProductPainPagesSection: React.FC = () => {
  const pages = [
    {
      title: "Автоматические выключатели",
      description: "Номинал, полюса, характеристика, отключающая способность, бренд, артикул и цена.",
      example: "Например: авт. выкл. 3P C25 6кА",
      cta: "Подобрать автоматы",
      path: "/podbor-avtomaticheskih-vyklyuchateley"
    },
    {
      title: "Кабель и провод",
      description: "Марка, сечение, число жил, исполнение, длина и подходящие позиции производителей.",
      example: "Например: ВВГнг(A)-LS 5х10",
      cta: "Подобрать кабель",
      path: "/podbor-kabelya-po-specifikacii"
    },
    {
      title: "Кабельные лотки",
      description: "Тип лотка, ширина, высота, покрытие, крышки, соединители, углы и крепеж.",
      example: "Например: лоток перф. 100х50",
      cta: "Подобрать лотки",
      path: "/podbor-kabelnyh-lotkov"
    },
    {
      title: "Щиты и шкафы",
      description: "Тип корпуса, габариты, IP, монтаж, количество модулей, материал и комплектация.",
      example: "Например: ЩРН-36 IP54",
      cta: "Подобрать щиты",
      path: "/podbor-shchitov-i-shkafov"
    },
    {
      title: "УЗО, дифавтоматы, контакторы",
      description: "Ток, полюса, ток утечки, тип, напряжение катушки, серия и артикул.",
      example: "Например: УЗО 4P 40А 30мА",
      cta: "Подобрать аппаратуру",
      path: "/podbor-uzo-difavtomatov-kontaktorov"
    },
    {
      title: "Поиск артикулов",
      description: "Разбор строки из Excel, PDF или проекта и переход к реальным товарам, брендам и ценам.",
      example: "Когда в спецификации нет артикула",
      cta: "Найти артикулы",
      path: "/poisk-artikulov-po-specifikacii"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mb-12">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-3">
            Категории подбора
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            Подбирайте не только общие позиции, но и конкретную электротехнику
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Загрузите спецификацию, Excel, PDF или список номенклатуры. Get-Goods.ai
            выделит технические параметры и поможет перейти к реальным изделиям:
            артикулам производителей, брендам, характеристикам и ценам.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pages.map((page) => (
            <Link
              key={page.path}
              to={page.path}
              className="group block bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-400 hover:shadow-lg transition-all duration-300"
            >
              <DocumentMagnifyingGlassIcon className="h-9 w-9 text-blue-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors duration-300">
                {page.title}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">{page.description}</p>
              <div className="text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 mb-5">
                {page.example}
              </div>
              <span className="inline-flex items-center text-blue-600 font-semibold">
                {page.cta}
                <ArrowLongRightIcon className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const SingleLineSchemesSection: React.FC = () => {
  const capabilities = [
    "поиск листов с однолинейными схемами внутри PDF-раздела ПД/РД",
    "распознавание автоматов, щитов, линий, кабелей, нагрузок и маркировок",
    "сбор распознанных позиций в спецификацию для дальнейшего подбора",
    "подбор электротехнических изделий по извлеченным характеристикам"
  ];

  return (
    <section id="single-line-schemes" className="py-20 bg-gray-900 text-white">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-300 mb-3">
              Новый сценарий для проектной документации
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Распознавание однолинейных электрических схем из PDF
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Get-Goods.ai обрабатывает PDF-раздел проектной документации по электрике:
              находит однолинейные схемы, извлекает оборудование и формирует основу
              для подбора по распознанной спецификации.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              Это помогает проектировщикам, снабжению и подрядчикам быстрее перейти
              от раздела ПД или рабочей документации к понятному перечню изделий,
              аналогов и вариантов закупки.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {capabilities.map((capability) => (
                <div key={capability} className="flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200">{capability}</span>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to="/raspoznavanie-odnolineynyh-shem"
                className="inline-flex items-center bg-white text-gray-900 hover:text-blue-700 px-6 py-3 rounded-lg text-base font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Подробнее о распознавании схем
                <DocumentMagnifyingGlassIcon className="h-5 w-5 ml-2" />
              </Link>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow-2xl">
            <img
              src="/assets/scheme_example.png"
              alt="Пример распознавания однолинейной электрической схемы из PDF проектной документации"
              className="w-full rounded-md border border-gray-200"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      icon: CloudArrowUpIcon,
      title: "Загрузите документ",
      description: "Загрузите спецификацию, однолинейную схему или PDF-раздел проектной документации",
      number: "01"
    },
    {
      icon: CpuChipIcon,
      title: "ИИ анализирует",
      description: "Система находит листы со схемами, извлекает позиции, обозначения и характеристики",
      number: "02"
    },
    {
      icon: DocumentMagnifyingGlassIcon,
      title: "Подбор изделий",
      description: "Алгоритмы сопоставляют распознанную спецификацию с каталогом электротехники",
      number: "03"
    },
    {
      icon: CheckCircleIcon,
      title: "Готовый результат",
      description: "Получите готовую спецификацию с артикулами и ценами",
      number: "04"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Как это работает
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Простой процесс от загрузки документа до готового результата
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center">
                {/* Connection line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-blue-300 to-purple-300 transform -translate-y-1/2 z-0" />
                )}
                
                <div className="relative z-10 bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-96 flex flex-col">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <step.icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-sm font-bold text-blue-600 mb-2">{step.number}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed flex-grow">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection: React.FC = () => {
  const audiences = [
    {
      title: "Снабжение",
      text: "Быстрее разобрать входящую спецификацию, получить список товаров и подготовить запрос цен."
    },
    {
      title: "Проектировщики",
      text: "Проверить, что позиции из проекта можно сопоставить с реальными изделиями и артикулами."
    },
    {
      title: "Подрядчики",
      text: "Перейти от раздела ПД/РД или Excel-перечня к закупочному списку без переписывания строк вручную."
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Для кого это сделано
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get-Goods.ai закрывает рутинную часть работы с электротехническими
            спецификациями: распознать, разобрать, сопоставить с товарами и ценами.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {audiences.map((audience, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 flex flex-col h-80"
            >
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-5">
                <CheckCircleIcon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{audience.title}</h3>
              <p className="text-gray-700 leading-relaxed flex-grow">{audience.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection: React.FC = () => {
  const { isAuthenticated } = useAppContext();

  return (
    <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
      <div className="container mx-auto px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Начните с текущей спецификации
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Загрузите файл с перечнем оборудования, спецификацией или PDF-разделом
            по электрике и получите основу для подбора артикулов, брендов и цен.
          </p>
          {isAuthenticated ? (
            <Link
              to="/my-applications"
              onClick={() => trackGoal('upload_click', { source: 'home_bottom_cta' })}
              className="inline-flex items-center bg-white text-blue-600 hover:text-blue-700 px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <CloudArrowUpIcon className="h-6 w-6 mr-2" />
              Загрузить файл
            </Link>
          ) : (
            <Link
              to="/login"
              onClick={() => trackGoal('login_click', { source: 'home_bottom_cta' })}
              className="inline-flex items-center bg-white text-blue-600 hover:text-blue-700 px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <SparklesIcon className="h-6 w-6 mr-2" />
              Начать работу бесплатно
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

const homePageJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Get-Goods.ai",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "url": "https://get-goods.ai/",
    "description": "ИИ-платформа для подбора электротехнических изделий по спецификациям, артикулам, брендам, ценам, PDF проектной документации и однолинейным электрическим схемам.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "RUB"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Можно ли распознать однолинейную электрическую схему из PDF?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Да. Get-Goods.ai находит однолинейные схемы в PDF проектной документации, извлекает оборудование и формирует данные для подбора электротехнических изделий."
        }
      },
      {
        "@type": "Question",
        "name": "Можно ли подобрать артикулы и цены по спецификации?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Да. Get-Goods.ai сопоставляет позиции спецификации с реальной электротехнической продукцией, артикулами производителей, брендами, аналогами и ценами из доступных источников."
        }
      }
    ]
  }
];

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-100">
      <SEO
        title="Get-Goods.ai - ИИ-подбор электротехники по спецификациям, артикулам и ценам"
        description="Get-Goods.ai подбирает реальные электротехнические изделия по спецификациям: артикулы производителей, бренды, аналоги, цены, а также распознает однолинейные схемы из PDF."
        canonicalPath="/"
        imagePath="/assets/scheme_example.png"
        keywords="подбор электротехники, подбор по спецификации, подбор артикулов, цены электротехники, бренды электротехники, распознавание однолинейных схем, раздел ПД электрика, ИИ для снабжения"
        jsonLd={homePageJsonLd}
      />
      <HomePageHeader />
      <main className="pt-20">
        <HeroSection />
        <FeaturesSection />
        <SpecificationMatchingSection />
        <ProductPainPagesSection />
        <SingleLineSchemesSection />
        <HowItWorksSection />
        <TestimonialsSection />
        <CTASection />
      </main>
    </div>
  );
};

export default HomePage;
